import { dateShapeOf } from './dateShape';
import { DATE_TOKEN } from './dateToken';

/**
 * A stored name or date, in the words a person reads — for Manage, on the phone and the web.
 *
 * Seen on the phone on 2026-09-29, Manage → Live for a café site: fields labelled "Startsat" and
 * "Endsat", a list headed "Menuitems", rows whose second line read "2026-10-26T14:00:00.000Z",
 * and a seeded shift that read "-3d 10:00". Each was a value shown in the form the CODE keeps
 * it in. The names went through `textTransform: 'capitalize'`, which on iOS lower-cases
 * everything after each word's first letter — so the capital that was the only mark of a
 * camelCase name's second word was erased. The browser showed the same names raw.
 *
 * These are for READING only. The editor shows a value through `displayValue`, whose text
 * `coerceValue` turns back into exactly what was stored; a readable date cannot be typed back
 * ("Mon, Oct 26, 2:00 PM" is not a shape the column holds), so it must never reach an input.
 *
 * Pure: no React, no platform — both clients import it (see web/src/__tests__/boundary.test.ts).
 */

/**
 * Written in capitals whatever the name did with them. Kept short on purpose: a word only
 * belongs here when its lower-case spelling is how an app names a field (`userId`, `imageUrl`)
 * and the capitalised word would read as a different word ("Id", "Url").
 */
const ACRONYMS = new Set(['id', 'url', 'faq', 'rsvp', 'pdf', 'sms', 'vip']);

/**
 * A field or list name as words: `startsAt` → "Starts at", `MENU_ITEMS` → "Menu items",
 * `imageUrl` → "Image URL".
 *
 * Sentence case, like every other label in Manage. A name already written as words is only
 * given its first capital. A name in capitals throughout (`const MENU = [ … ]`) is shouting,
 * not an acronym, and is read as ordinary words.
 *
 * A dot joins a content bank to one of its groups — content.ts offers `var BANK = { Science:
 * [ … ] }` as `BANK.Science` — so each side is read on its own: "Bank · Science".
 */
export function humanize(name: string): string {
  if (name.includes('.')) return name.split('.').map(phrase).filter(Boolean).join(' · ');
  return phrase(name);
}

/** One name, no dot, in words. */
function phrase(name: string): string {
  const words = name
    // `startsAt` → `starts At`; `q4Revenue` → `q4 Revenue`.
    .replace(/([\p{Ll}\d])(\p{Lu})/gu, '$1 $2')
    // `HTMLContent` → `HTML Content`: the last capital of a run starts the next word — unless
    // all that follows it is a lone plural 's'. Review of the Manage-labels merge found `FAQs`
    // headed "FA qs", `URLs` "UR ls" and `imageURLs` "Image UR ls": a plural acronym is one word.
    .replace(/(\p{Lu})(?!\p{Lu}s(?!\p{Ll}))(\p{Lu}\p{Ll})/gu, '$1 $2')
    .split(/[\s_-]+/)
    .filter(Boolean);
  if (words.length === 0) return '';
  const upper = (w: string) => /\p{L}/u.test(w) && w === w.toUpperCase();
  const shouting = words.every(upper);
  return words
    .map((word, i) => {
      const lower = word.toLowerCase();
      if (ACRONYMS.has(lower)) return lower.toUpperCase();
      // The plural of one, however it was cased: `imageUrls`, `userIds`, `faqs` → "URLs", "IDs", "FAQs".
      if (lower.endsWith('s') && ACRONYMS.has(lower.slice(0, -1))) return `${lower.slice(0, -1).toUpperCase()}s`;
      // `imageURL`, `HTMLContent`, `APIs`: capitals inside a name that is not all capitals are
      // meant — a plural acronym's 's' included.
      if (!shouting && word.length > 1 && (upper(word) || /^\p{Lu}{2,}s$/u.test(word))) return word;
      return i === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
    })
    .join(' ');
}

/*
 * A relative seed token is what a generated app writes into a starting row for a date —
 * '+3d 18:30' — and it is resolved against "now" when the row is published or rendered. So a row
 * can still hold one: a list written into the page keeps its token for good, and the page
 * resolves it each time it is read. Manage reads it with the publisher's own DATE_TOKEN,
 * imported rather than copied, so it dates exactly the tokens the publisher and the page do.
 */
const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

/** The moment a token lands on from `now` — the page's own arithmetic (runtime/js.ts parseDate). */
function resolveToken(m: RegExpExecArray, now: Date): Date {
  const amount = Number(m[1]);
  const unit = m[2].toLowerCase();
  const when = new Date(now.getTime());
  if (unit === 'h') when.setHours(when.getHours() + amount);
  else when.setDate(when.getDate() + amount * (unit === 'w' ? 7 : 1));
  if (m[3] !== undefined) {
    const target = DAYS.indexOf(m[3].toLowerCase());
    when.setDate(when.getDate() + ((target - when.getDay() + 7) % 7));
  }
  if (m[4] !== undefined) when.setHours(Number(m[4]), Number(m[5]), 0, 0);
  return when;
}

/**
 * A token pointing back — '-3d 10:00' — which the page cannot read.
 *
 * It is what reached the phone on 2026-09-29: a seeded "already past" shift. DATE_TOKEN has no
 * minus and neither has the page's parseDate, so the live page shows NO date for that row. This
 * screen used to resolve it anyway, to "Sat, Sep 26, 10:00 AM" — so Manage, where the owner
 * checks the page, showed the one broken row as the healthy one, with a date that moved every
 * day. It is said as what it is instead, token included, so the owner can find it and fix it.
 */
const backward = (s: string) => s.startsWith('-') && DATE_TOKEN.test(`+${s.slice(1)}`);

const DAY_PARTS = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/;

/**
 * A stored date as a person reads it — "Mon, Oct 26, 2:00 PM" in the viewer's own locale and
 * time — or null when the value is not a date. A token pointing back, which the page shows no
 * date for, is said to be one: "No date the page can show (-3d 10:00)".
 *
 * Every shape a column holds (see dateShape.ts) and a relative token. A calendar date is that
 * day wherever it is read (built from its parts, never `new Date('2026-10-26')`, which is UTC
 * midnight and the 25th west of Greenwich) and has no clock. The year is said only when it is
 * not this one. `now` is the clock a token and "this year" are read against.
 *
 * A bare clock time ('18:30') is NOT read as one: a run's pace of 5:30 is written the same way
 * and is not half past five in the morning, and '18:30' is already readable as it is.
 */
export function readableDate(value: unknown, options: { locale?: string; now?: Date } = {}): string | null {
  if (typeof value !== 'string') return null;
  const s = value.trim();
  const now = options.now ?? new Date();

  if (backward(s)) return `No date the page can show (${s})`;

  let at: Date;
  let clock: boolean;
  const token = DATE_TOKEN.exec(s);
  if (token) {
    at = resolveToken(token, now);
    clock = token[2].toLowerCase() === 'h' || token[4] !== undefined;
  } else {
    const shape = dateShapeOf(s);
    if (shape === 'instant') {
      at = new Date(s);
      clock = true;
    } else if (shape === 'date' || shape === 'local') {
      // Local parts, read by hand: a zoneless string's parsing differs between engines.
      const p = DAY_PARTS.exec(s)!;
      clock = shape === 'local';
      at = new Date(Number(p[1]), Number(p[2]) - 1, Number(p[3]), Number(p[4] ?? 0), Number(p[5] ?? 0), Number(p[6] ?? 0));
      // 2026-02-30 rolls over to 2 March; a day that does not exist is not a date to show.
      if (at.getMonth() !== Number(p[2]) - 1 || at.getDate() !== Number(p[3])) return null;
    } else {
      return null;
    }
  }
  if (Number.isNaN(at.getTime())) return null;

  const format: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric' };
  if (at.getFullYear() !== now.getFullYear()) format.year = 'numeric';
  if (clock) {
    format.hour = 'numeric';
    format.minute = '2-digit';
  }
  return at.toLocaleString(options.locale, format);
}
